import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '@/components/ui/select';
import { useTheme } from '@/context/ThemeContext';
import { 
  API_URL, EQUIPMENT_TYPES, COMMON_ACCESSORIES, formatCurrency 
} from '@/lib/utils';
import { toast } from 'sonner';
import {
  Wrench, Moon, Sun, ArrowLeft, ArrowRight, User, Laptop,
  Package, Settings, DollarSign, Check, Loader2, PlusCircle, AlertCircle
} from 'lucide-react';

const STEPS = [
  { id: 1, title: 'Cliente', icon: User },
  { id: 2, title: 'Equipo', icon: Laptop },
  { id: 3, title: 'Accesorios', icon: Package },
  { id: 4, title: 'Servicios', icon: Settings },
  { id: 5, title: 'Presupuesto', icon: DollarSign },
];

export default function NewOrderPage() {
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState([]);

  const [formData, setFormData] = useState({
    client: { name: '', phone: '', email: '' },
    equipment: { type: '', brand: '', model: '', serial: '', aesthetic_condition: '' },
    accessories: [],
    services: [],
    reported_issue: '',
    services_cost: 0,
    additional_cost: 0,
    estimated_cost: 0,
    advance_payment: 0 
  });

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API_URL}/services`);
      setServices(response.data);
    } catch (error) {
      toast.error('Error al cargar catálogo de servicios');
    }
  };

  const updateFormData = (section, field, value) => {
    if (section) {
      setFormData(prev => ({
        ...prev,
        [section]: { ...prev[section], [field]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const toggleAccessory = (accessory) => {
    setFormData(prev => ({
      ...prev,
      accessories: prev.accessories.includes(accessory)
        ? prev.accessories.filter(a => a !== accessory)
        : [...prev.accessories, accessory]
    }));
  };

  const calculateTotals = (selectedIds, extra) => {
    const selected = services.filter(s => selectedIds.includes(s.service_id));
    const sCost = selected.reduce((total, s) => total + s.base_price, 0);
    const additional = parseFloat(extra) || 0;
    
    setFormData(prev => ({
      ...prev,
      services: selectedIds,
      services_cost: sCost,
      additional_cost: additional,
      estimated_cost: sCost + additional
    }));
  };

  const validateStep = () => {
    if (currentStep === 1 && (!formData.client.name || !formData.client.phone)) {
      toast.error('Nombre y teléfono son obligatorios');
      return false;
    }
    if (currentStep === 2 && (!formData.equipment.type || !formData.equipment.model)) {
      toast.error('Tipo y modelo son obligatorios');
      return false;
    }
    if (currentStep === 4 && formData.services.length === 0 && formData.additional_cost === 0) {
      toast.error('Seleccione un servicio o ingrese un costo extra');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!formData.reported_issue) return toast.error('Describa la falla del equipo');
    
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/orders`, formData);
      toast.success(`Orden creada correctamente`);
      navigate(`/admin/orders/${response.data.order_id}`);
    } catch (error) {
      console.error("Detalle del error:", error);
      
      // FIX para el error "Objects are not valid as a React child"
      let errorDetail = error.response?.data?.detail;
      
      if (Array.isArray(errorDetail)) {
        // Extrae el mensaje de validación de Pydantic
        errorDetail = errorDetail[0]?.msg || 'Error de validación en los datos';
      } else if (typeof errorDetail === 'object') {
        errorDetail = 'Error en los datos enviados al servidor';
      }

      toast.error(errorDetail || 'Error al guardar la orden');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-20">
      <nav className="border-b bg-background/80 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 flex items-center justify-between h-16">
          <Button variant="ghost" onClick={() => navigate('/admin')}>
            <ArrowLeft className="mr-2 h-4 w-4" /> Volver
          </Button>
          <div className="flex items-center gap-2">
            <Wrench className="h-5 w-5 text-primary" />
            <span className="font-bold uppercase tracking-tighter">Nueva Orden</span>
          </div>
          <Button variant="ghost" size="icon" onClick={toggleTheme}>
            {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Stepper Visual */}
        <div className="flex justify-between mb-10 overflow-x-auto pb-4 gap-2">
          {STEPS.map((step) => (
            <div key={step.id} className="flex flex-col items-center flex-1 min-w-[80px]">
              <div className={`w-10 h-10 rounded-full border-2 flex items-center justify-center transition-all ${
                currentStep === step.id ? 'border-primary bg-primary text-primary-foreground scale-110' : 
                currentStep > step.id ? 'border-primary bg-primary/20 text-primary' : 'border-muted text-muted-foreground'
              }`}>
                {currentStep > step.id ? <Check className="h-5 w-5" /> : <step.icon className="h-5 w-5" />}
              </div>
              <span className={`text-[10px] mt-2 font-black uppercase text-center ${currentStep === step.id ? 'text-primary' : 'text-muted-foreground'}`}>
                {step.title}
              </span>
            </div>
          ))}
        </div>

        <Card className="border-2 shadow-2xl overflow-hidden">
          <CardHeader className="bg-muted/50 border-b">
            <CardTitle className="text-lg font-bold">{STEPS[currentStep - 1].title}</CardTitle>
            <CardDescription>Paso {currentStep} de 5</CardDescription>
          </CardHeader>

          <CardContent className="p-6">
            {/* PASO 1: CLIENTE */}
            {currentStep === 1 && (
              <div className="space-y-4 animate-in fade-in slide-in-from-right-4">
                <div className="space-y-2">
                  <Label>Nombre completo del cliente *</Label>
                  <Input 
                    placeholder="Ej. Juan Pérez"
                    value={formData.client.name} 
                    onChange={(e) => updateFormData('client', 'name', e.target.value)} 
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>WhatsApp / Teléfono *</Label>
                    <Input 
                      placeholder="Número de contacto"
                      value={formData.client.phone} 
                      onChange={(e) => updateFormData('client', 'phone', e.target.value)} 
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Correo electrónico</Label>
                    <Input 
                      type="email"
                      placeholder="cliente@ejemplo.com"
                      value={formData.client.email} 
                      onChange={(e) => updateFormData('client', 'email', e.target.value)} 
                    />
                  </div>
                </div>
              </div>
            )}

            {/* PASO 2: EQUIPO */}
            {currentStep === 2 && (
              <div className="space-y-4 animate-in fade-in slide-in-from-right-4">
                <div className="space-y-2">
                  <Label>Tipo de Dispositivo *</Label>
                  <Select onValueChange={(v) => updateFormData('equipment', 'type', v)} value={formData.equipment.type}>
                    <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                    <SelectContent>
                      {EQUIPMENT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Marca</Label>
                    <Input value={formData.equipment.brand} onChange={(e) => updateFormData('equipment', 'brand', e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Modelo *</Label>
                    <Input value={formData.equipment.model} onChange={(e) => updateFormData('equipment', 'model', e.target.value)} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Estado Estético / Observaciones Iniciales</Label>
                  <Textarea 
                    placeholder="Rayones, golpes, falta de tornillos..."
                    value={formData.equipment.aesthetic_condition}
                    onChange={(e) => updateFormData('equipment', 'aesthetic_condition', e.target.value)}
                  />
                </div>
              </div>
            )}

            {/* PASO 3: ACCESORIOS */}
            {currentStep === 3 && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 animate-in fade-in slide-in-from-right-4">
                {COMMON_ACCESSORIES.map(item => (
                  <div 
                    key={item}
                    onClick={() => toggleAccessory(item)}
                    className={`p-4 border-2 rounded-xl cursor-pointer flex items-center gap-3 transition-all ${
                      formData.accessories.includes(item) ? 'border-primary bg-primary/5' : 'border-muted hover:border-primary/30'
                    }`}
                  >
                    <Checkbox checked={formData.accessories.includes(item)} />
                    <span className="text-sm font-medium">{item}</span>
                  </div>
                ))}
              </div>
            )}

            {/* PASO 4: SERVICIOS Y COSTOS EXTRAS */}
            {currentStep === 4 && (
              <div className="space-y-6 animate-in fade-in slide-in-from-right-4">
                <div className="grid gap-3">
                  <Label className="text-xs font-bold uppercase opacity-60">Mano de Obra / Catálogo</Label>
                  {services.map(s => (
                    <div 
                      key={s.service_id}
                      onClick={() => {
                        const newIds = formData.services.includes(s.service_id)
                          ? formData.services.filter(id => id !== s.service_id)
                          : [...formData.services, s.service_id];
                        calculateTotals(newIds, formData.additional_cost);
                      }}
                      className={`p-4 border-2 rounded-xl cursor-pointer flex justify-between items-center transition-all ${
                        formData.services.includes(s.service_id) ? 'border-primary bg-primary/5' : 'border-muted'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Checkbox checked={formData.services.includes(s.service_id)} />
                        <div>
                          <p className="font-bold text-sm">{s.name}</p>
                          <p className="text-xs text-muted-foreground">{s.description}</p>
                        </div>
                      </div>
                      <span className="font-mono font-bold text-primary">{formatCurrency(s.base_price)}</span>
                    </div>
                  ))}
                </div>

                <div className="p-4 rounded-xl border-2 border-dashed border-amber-500/50 bg-amber-500/5">
                  <Label className="text-amber-600 font-bold flex items-center gap-2 mb-2">
                    <PlusCircle className="h-4 w-4" /> ¿Costo de Repuestos o Extras?
                  </Label>
                  <Input 
                    type="number" 
                    placeholder="0.00"
                    value={formData.additional_cost || ''}
                    onChange={(e) => calculateTotals(formData.services, e.target.value)}
                  />
                </div>

                <div className="bg-primary p-4 rounded-xl flex justify-between items-center text-primary-foreground shadow-lg">
                  <span className="font-bold text-sm uppercase">Total Estimado:</span>
                  <span className="text-2xl font-black">{formatCurrency(formData.estimated_cost)}</span>
                </div>
              </div>
            )}

            {/* PASO 5: CONFIRMACIÓN Y ANTICIPO */}
            {currentStep === 5 && (
              <div className="space-y-6 animate-in fade-in slide-in-from-right-4">
                <div className="space-y-2">
                  <Label className="font-bold flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-primary" /> Falla que reporta el cliente *
                  </Label>
                  <Textarea 
                    rows={4}
                    placeholder="Detalla el problema que presenta el equipo..."
                    value={formData.reported_issue}
                    onChange={(e) => updateFormData(null, 'reported_issue', e.target.value)}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 border-2 rounded-xl bg-muted/30">
                    <Label className="text-xs font-bold uppercase opacity-60">Presupuesto Final</Label>
                    <p className="text-2xl font-black">{formatCurrency(formData.estimated_cost)}</p>
                  </div>
                  <div className="p-4 border-2 rounded-xl border-green-500/20 bg-green-500/5">
                    <Label className="text-xs font-bold uppercase text-green-600">Anticipo Recibido</Label>
                    <Input 
                      type="number" 
                      className="mt-2 text-lg font-bold"
                      placeholder="0.00"
                      value={formData.advance_payment || ''}
                      onChange={(e) => updateFormData(null, 'advance_payment', parseFloat(e.target.value) || 0)}
                    />
                  </div>
                </div>

                <div className="flex justify-between items-center p-4 bg-red-500/5 border border-red-500/20 rounded-xl">
                  <span className="text-sm font-bold text-red-600 uppercase">Saldo Pendiente:</span>
                  <span className="text-3xl font-black text-red-600">
                    {formatCurrency(formData.estimated_cost - formData.advance_payment)}
                  </span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Navegación Inferior */}
        <div className="flex justify-between mt-8">
          <Button variant="outline" size="lg" onClick={() => setCurrentStep(s => s - 1)} disabled={currentStep === 1 || loading}>
            Atrás
          </Button>

          {currentStep < 5 ? (
            <Button size="lg" onClick={() => validateStep() && setCurrentStep(s => s + 1)}>
              Siguiente <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          ) : (
            <Button 
              size="lg" 
              className="bg-primary px-10 shadow-xl"
              onClick={handleSubmit} 
              disabled={loading}
            >
              {loading ? <Loader2 className="animate-spin mr-2 h-4 w-4" /> : <Check className="mr-2 h-4 w-4" />} 
              Finalizar Orden
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}